import { getApiBase, getSiteBase, dimensionToWorld } from './config';
import type { PhotoPoint } from './types';

/**
 * Load geo photo points for map3.
 * Tries several bases: same-origin /api (map nginx → staging API), sibling site, static fallback.
 */
export async function loadPhotoPoints(): Promise<PhotoPoint[]> {
  const bases = apiBases();
  const paths = ['/map/photo-points?limit=1000', '/screenshots?limit=500'];

  for (const base of bases) {
    for (const path of paths) {
      const url = `${base.replace(/\/$/, '')}${path}`;
      try {
        const res = await fetch(url, {
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) continue;
        const data = await res.json();
        if (Array.isArray(data?.points)) {
          const pts = data.points.map(normalizePoint).filter(Boolean) as PhotoPoint[];
          if (pts.length) return pts;
        }
        if (Array.isArray(data?.screenshots)) {
          const pts = data.screenshots
            .map(normalizeScreenshot)
            .filter(Boolean) as PhotoPoint[];
          if (pts.length) return pts;
        }
      } catch {
        /* try next */
      }
    }
  }

  // Last resort: static snapshot next to SPA (warmed on deploy)
  try {
    const res = await fetch(`${window.location.origin}/photo-points.json`, {
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data?.points)) {
        return data.points.map(normalizePoint).filter(Boolean) as PhotoPoint[];
      }
    }
  } catch {
    /* ignore */
  }

  return [];
}

function apiBases(): string[] {
  const params = new URLSearchParams(window.location.search);
  const override = params.get('api');
  const list: string[] = [];
  if (override) list.push(override.replace(/\/$/, ''));

  const primary = getApiBase();
  if (!list.includes(primary)) list.push(primary);

  // Sibling site (map3.reborned.one → reborned.one) may host prod API later
  const site = getSiteBase();
  const siblingApi = `${site.replace(/\/$/, '')}/api`;
  if (!list.includes(siblingApi) && !siblingApi.includes('localhost')) {
    list.push(siblingApi);
  }

  // Staging API host (where geo seeds currently live)
  if (!list.includes('https://staging.reborned.one/api')) {
    list.push('https://staging.reborned.one/api');
  }

  return list;
}

function normalizePoint(raw: Record<string, unknown>): PhotoPoint | null {
  const x = Number(raw.x ?? (raw.coordinates as { x?: number } | undefined)?.x);
  const y = Number(raw.y ?? (raw.coordinates as { y?: number } | undefined)?.y);
  const z = Number(raw.z ?? (raw.coordinates as { z?: number } | undefined)?.z);
  if (![x, y, z].every(Number.isFinite)) return null;
  const dimension = (raw.dimension as string) || null;
  return {
    id: Number(raw.id),
    x,
    y,
    z,
    dimension,
    world: String(raw.world || dimensionToWorld(dimension)),
    image: String(raw.image || ''),
    player: String(raw.player || 'Игрок'),
    title: String(raw.title || 'Скриншот'),
    published_at: (raw.published_at as string) || null,
  };
}

function normalizeScreenshot(raw: Record<string, unknown>): PhotoPoint | null {
  const coords = raw.coordinates as { x?: number; y?: number; z?: number } | null;
  if (!coords && !raw.has_geo) return null;
  return normalizePoint({
    ...raw,
    x: coords?.x,
    y: coords?.y,
    z: coords?.z,
  });
}
